import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";
//import { GridOptions, ColDef, AllModules, GridApi } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { Subscription, forkJoin, of, Observable } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { LandownerPassthroughProgramApplication, LandownerProgramSearchSpec, LandownerProgramStatus, LandownerProgramQueryResult, LandownerProgramEvents } from "../../../../common/models/permit.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { ButtonRendererComponent } from "../../../../common/components/ag-grid/button-renderer.component";
import { PermitService } from "../../../../common/services/permit.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { TabComponent } from "../../../../common/components/tabs/tab.component";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";

@Component({
    selector: 'app-view-passthrough-permits',
    templateUrl: './view-passthrough-permits.component.html',
    styleUrls: ['./view-passthrough-permits.component.scss'],
    standalone: false
})
export class ViewPassthroughPermitsComponent implements OnInit, OnDestroy, AfterViewInit {

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }


	activeGridOptions: GridOptions;
	activeGridApi?: GridApi;
    exitedGridOptions: GridOptions;
	exitedGridApi?: GridApi;
    active: LandownerProgramQueryResult[];
    currentTab: string = "Active Wells";
    exited: LandownerProgramQueryResult[];
    exportDialog: Dialog = new Dialog("Do you want to export all data, or only the currently selected tab?", [new DialogButton("Cancel", false, () => { return; }), new DialogButton("All Data", true, () => { this.exportAll(); }), new DialogButton("Only Selected", true, () => { this.exportActive(); })]);
    subscription: Subscription = new Subscription();

    statuses: any = [];
    dispositions: any = [];

	constructor(private router: Router, private datePipe: DatePipe, private formatter: GridFormatterService, private permitService: PermitService, private dialogService: DynamicDialogService, private subheader: SubHeaderService,
		private domainService: DomainDefinitionService) { }

    ngOnInit() {
        this.activeGridOptions = <GridOptions>{
            columnDefs: this.createColumnDefinitions(true),
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.activeGridApi = params.api; }
        };
        this.exitedGridOptions = <GridOptions>{
            columnDefs: this.createColumnDefinitions(false),
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.exitedGridApi = params.api; }
        };
        this.subheader.updateSubHeader({ title: "View Pass-through Program Permits", buttons: [{ name: "Export", method: () => this.exportData() }]})
        this.search();
       
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    ngAfterViewInit() {
        if (this.currentTab == 'Active Wells') { this.activeGridApi.sizeColumnsToFit(); }
        else { this.exitedGridApi.sizeColumnsToFit(); }
    }


    createColumnDefinitions(isActiveTab: boolean): ColDef[] {
        const columns: ColDef[] = [];
        columns.push({
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "applicationId",
            tooltipField: "applicationId",
            minWidth: 125,
            maxWidth: 125
        } as ColDef);
        if (isActiveTab == false) {
            columns.push({
                headerClass: "wrapped",
                headerName: "Program Status",
                headerTooltip: "Program Status",
                field: "programStatus",
                tooltipField: "programStatus",
                minWidth: 125,
                maxWidth: 125
            } as ColDef);
        }
        columns.push({
            headerClass: "wrapped",
            headerName: "API #",
            headerTooltip: "API #",
            field: "wellId",
            tooltipField: "wellId",
            minWidth: 125,
            maxWidth: 125,
            cellClass: "stringType"
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            field: "passthroughProgramWell.wellName",
            tooltipField: "passthroughProgramWell.wellName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType"
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Well Number",
            headerTooltip: "Well Number",
            field: "passthroughProgramWell.wellNumber",
            tooltipField: "passthroughProgramWell.wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType"
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Well Status",
            headerTooltip: "Well Status",
            valueGetter: this.formatStatus(),
            tooltipValueGetter: this.formatStatus(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType"
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Disposition",
            headerTooltip: "Disposition",
            valueGetter: this.formatDisposition(),
            tooltipValueGetter: this.formatDisposition(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType"
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Date Application Submitted",
            headerTooltip: "Date Application Submitted",
            valueGetter: this.getEventDate(LandownerProgramEvents.Submitted, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Date Permit Approved",
            headerTooltip: "Date Permit Approved",
            valueGetter: this.getEventDate(LandownerProgramEvents.Approved, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Approved, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Approved, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Land Owner Name",
            headerTooltip: "Land Owner Name",
            field: "passthroughProgramLandowner.ownerName",
            tooltipField: "passthroughProgramLandowner.ownerName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Contractor Name",
            headerTooltip: "Contractor Name",
            field: "passthroughProgramContractor.contractorName",
            tooltipField: "passthroughProgramContractor.contractorName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Plugging Estimate",
            headerTooltip: "Plugging Estimate",
            field: "contractorEstimate",
            tooltipValueGetter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            valueFormatter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            filter: "agNumberColumnFilter",
            filterParams: this.formatter.defaultNumberFilterParams,
            minWidth: 150
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            field: "passthroughProgramWell.county",
            tooltipField: "passthroughProgramWell.county",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Township",
            headerTooltip: "Township",
            field: "passthroughProgramWell.township",
            tooltipField: "passthroughProgramWell.township",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Section",
            headerTooltip: "Section",
            field: "passthroughProgramWell.section",
            tooltipField: "passthroughProgramWell.section",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Lot",
            headerTooltip: "Lot",
            field: "passthroughProgramWell.lot",
            tooltipField: "passthroughProgramWell.lot",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100
        } as ColDef);
        columns.push({
            headerClass: "wrapped",
            headerName: "Tract",
            headerTooltip: "Tract",
            field: "passthroughProgramWell.tract",
            tooltipField: "passthroughProgramWell.tract",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100
        } as ColDef);
        if (isActiveTab) {
            columns.push({
                headerClass: "wrapped",
                headerName: "Next Action",
                headerTooltip: "Next Action",
                cellRenderer: ButtonRendererComponent,
                cellRendererParams: {
                    buttonText: "Complete",
                    action: (obj) => this.view(obj)
                },
                minWidth: 150,
                maxWidth: 150
            } as ColDef);
        }

        this.formatter.applyFilterKeyboardHintTooltips(columns);
        return columns;
    }

    exportActive(): void {
        const currentDate = this.datePipe.transform(new Date(), "yyyy-MM-dd");
        const fileName = `${this.currentTab.replace(' ', '_')}_${currentDate}`;
        const sheetName = `${this.currentTab}`;
        const api: GridApi | undefined = (this.currentTab == 'Active Wells') ? this.activeGridApi : this.exitedGridApi;
        this.formatter.exportDataToExcel(fileName, sheetName, api);
    }

    exportAll(): void {
        const currentDate = this.datePipe.transform(new Date(), "yyyy-MM-dd");
        let fileName = `Active_Wells_${currentDate}`;
        let sheetName = `Active Wells`;
        let api: GridApi | undefined = this.activeGridApi;
        this.formatter.exportDataToExcel(fileName, sheetName, api);
        fileName = `Exited_Wells_${currentDate}`;
        sheetName = `Exited Wells`;
        api = this.exitedGridApi;
        this.formatter.exportDataToExcel(fileName, sheetName, api);
    }

    exportData(): void {
        this.exportDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.exportDialog);
    }

    getEventDate(event: LandownerProgramEvents, formatAsString: boolean): any {
        return (obj) => {
            let value = "N/A";
            if (obj.data != null) {
                if (obj.data.landownerPassthroughProgramHistories != null && obj.data.landownerPassthroughProgramHistories.length > 0) {
                    let submissions = obj.data.landownerPassthroughProgramHistories.filter(h => h.eventTypeDescription === event)
                    if (submissions != null && submissions.length > 0) {
                        submissions = submissions.sort((a, b) => {
                            if (a.recordedDate < b.recordedDate) return -1;
                            if (a.recordedDate > b.recordedDate) return 1;
                            return 0;
                        });
                        value = submissions[0].recordedDate;
                    } else {
                        if (event === LandownerProgramEvents.Approved) {
                            value = obj.data.lastUpdated;
                        } else {
                            value = obj.data.dateCreated;
                        }
                    }

                }
                else {
                    if (event === LandownerProgramEvents.Approved) {
                        value = obj.data.lastUpdated;
                    } else {
                        value = obj.data.dateCreated;
                    }
                }

            }
            if (value != null && value.length > 0 && formatAsString) value = this.datePipe.transform(value, "MM/dd/yyyy hh:mm a");
            return value;
        }
    }

    search(): void {
        let spec = {
            apiNumbers: [],
            programStatus: [LandownerProgramStatus.Active, LandownerProgramStatus.Plugged, LandownerProgramStatus.Withdrawn],
            landowner: "",
            contractor: "",
            includeStatusAndDisposition: true
        } as LandownerProgramSearchSpec;

        let statusObs$: Observable<any> = (this.statuses == null || this.statuses.length == 0)
            ? this.domainService.getValues("WL_STATUS").pipe(
                tap((data: any) => {
                    this.statuses = data;
                })
            )
            : of(this.statuses);

        let descriptionObs$: Observable<any> = (this.dispositions == null || this.dispositions.length == 0)
            ? this.domainService.getValues("STATUS_DESC").pipe(tap((data: any) => {
                this.dispositions = data;
            })
            )
            : of(this.dispositions);

        const dataObs$: Observable<LandownerProgramQueryResult[]> = this.permitService.searchLandownerPassthroughApplications(spec).pipe(
            tap((data: LandownerProgramQueryResult[]) => {
                this.active = data.filter(d => d.programStatus == LandownerProgramStatus.Active);
                this.exited = data.filter(d => d.programStatus == LandownerProgramStatus.Plugged || d.programStatus == LandownerProgramStatus.Withdrawn);
            })
        )

        const dataSubs$ = forkJoin(statusObs$, descriptionObs$).pipe(
            switchMap(() => {
                return dataObs$;
            })
        ).subscribe(
            () => {
                if (this.currentTab == 'Active Wells') { this.activeGridApi.sizeColumnsToFit(); }
                else { this.exitedGridApi.sizeColumnsToFit(); }
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(dataSubs$);
    }

    formatDisposition(): any {
        return (obj) => {
            let value = "";
            if (obj.data && obj.data.wellDisposition != null) {
                value = obj.data.wellDisposition;
                let definition = this.dispositions.find(s => s.Code == value);
                if (definition != null) {
                    value = definition.Definition;
                }
            }
            return value;
        }
    }

    formatStatus(): any {
        return (obj) => {
            let value = "";
            if (obj.data && obj.data.wellStatus != null) {
                value = obj.data.wellStatus;
                let definition = this.statuses.find(s => s.Code == value);
                if (definition != null) {
                    value = definition.Definition;
                }
            }
            return value;
        }
    }

    onTabClicked(tab: TabComponent): void {
        this.currentTab = tab.title;
        if (this.currentTab == 'Active Wells') { this.activeGridApi.sizeColumnsToFit(); }
        else { this.exitedGridApi.sizeColumnsToFit(); }

    }

    view(obj: LandownerPassthroughProgramApplication): void {
        this.router.navigate(["/admin/landowner-passthrough/complete", obj.applicationId]);
    }
}
